import clsx from 'clsx';
import React, { useMemo } from 'react';
import { match, P } from 'ts-pattern';

import { type CurrencyCode, currencyCodes } from '@fragment/types';

import { TertiaryButton } from '../Button/TertiaryButton/TertiaryButton';
import { Dropdown } from '../Dropdown/Dropdown';
import { Icon } from '../Icon';
import { TextInput } from '../Input/TextInput';
import { CurrencyField } from './CurrencyField';
import { kBoxWidth } from './LayoutRow';

const SpanOrDropdown = ({
  value,
  onChange,
  options,
  'data-testid': dataTestId,
}: {
  value: string;
  onChange?: (value: string) => void;
  options?: { value: string; label: string }[];
  'data-testid'?: string;
}) =>
  match({ onChange, options })
    .with(
      { onChange: P.nonNullable, options: P.when((o) => !!o && o.length > 0) },
      ({ onChange: onChangeFn, options: opts }) => (
        <Dropdown
          options={opts!}
          onChange={onChangeFn}
          displayValue={value}
          size="md"
          width="w-full"
          height="h-fit"
          align="left"
          data-testid={dataTestId}
        />
      )
    )
    .otherwise(() => (
      <div className="flex flex-row w-full h-fit">
        <span className="text-md text-main">{value}</span>
      </div>
    ));

const TextInputOrSpan = ({
  value,
  onChange,
  placeholder,
  'data-testid': dataTestId,
}: {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  'data-testid'?: string;
}) =>
  match(onChange)
    .with(P.nonNullable, (onChangeFn) => (
      <TextInput
        value={value}
        onChange={(e) => onChangeFn(e.target.value)}
        size="md"
        padding="p-f0"
        width="w-full"
        height="h-fit"
        align="left"
        transparent
        placeholder={placeholder}
        data-testid={dataTestId}
      />
    ))
    .otherwise(() => <span className="text-md text-main">{value}</span>);

const EditableCurrencyField = ({
  amount,
  currencyPrecision,
  currencyCode,
  setAmount,
  setCurrency,
  'data-testid': dataTestId,
}: {
  amount: number;
  currencyPrecision: number;
  currencyCode: CurrencyCode;
  setAmount?: (amount: number) => void;
  setCurrency?: (currency: CurrencyCode) => void;
  'data-testid'?: string;
}) =>
  match({ setAmount, setCurrency })
    .with({ setAmount: P.nullish, setCurrency: P.nullish }, () => (
      <CurrencyField
        amount={amount}
        currencyPrecision={currencyPrecision}
        currencyCode={currencyCode}
        textSize="md"
        align="right"
      />
    ))
    .otherwise(() => (
      <div className="relative flex flex-row w-full h-full items-center justify-end gap-x-[1ch] overflow-y-visible">
        <TextInput
          type="number"
          value={amount}
          onChange={(e) => setAmount?.(Number(e.target.value))}
          size="md"
          padding="p-0"
          width="w-full"
          height="h-fit"
          align="right"
          data-testid={dataTestId}
        />
        <Dropdown
          options={currencyCodes
            .filter(
              (code: CurrencyCode) => code !== 'LOGICAL' && code !== 'CUSTOM'
            )
            .map((code: CurrencyCode) => ({
              value: code,
              label: code,
            }))}
          onChange={(value) => setCurrency?.(value as CurrencyCode)}
          displayValue={currencyCode}
          size="md"
          width="w-fit"
          height="h-fit"
          align="right"
        />
      </div>
    ));

export type InvoiceRowItem = {
  id: string;
  productCode: string;
  payoutUser: string;
  description: string;
  amount: number;
  currency: CurrencyCode;
  currencyPrecision: number;
  balance?: number;
};

const RowLayout = ({
  showBalance,
  children,
  paddingTop,
}: {
  showBalance: boolean;
  children: React.ReactNode;
  paddingTop?: boolean;
}) => (
  <div
    className={clsx(
      showBalance ? 'grid grid-cols-4' : 'grid grid-cols-3',
      ' w-full h-fit min-h-f4 px-f1 pb-f1 items-start gap-x-f1 gap-y-f1',
      paddingTop ? 'pt-f1' : ''
    )}
  >
    {children}
  </div>
);

const InvoiceItemRow = ({
  item,
  showBalance,
  removeInvoiceItem,
  setPrice,
  setCurrency,
  setDescription,
  setPayoutUser,
  setProductCode,
  productOptions,
  payoutUserOptions,
  rowIndex,
}: {
  item: InvoiceRowItem;
  showBalance: boolean;
  removeInvoiceItem?: () => void;
  setPrice?: (amount: number) => void;
  setCurrency?: (currency: CurrencyCode) => void;
  setDescription?: (description: string) => void;
  setPayoutUser?: (payoutUser: string) => void;
  setProductCode?: (productCode: string) => void;
  productOptions?: { value: string; label: string }[];
  payoutUserOptions?: { value: string; label: string }[];
  rowIndex?: number;
}) => (
  <div
    className="flex flex-row w-full h-fit"
    data-testid={
      rowIndex !== undefined ? `invoice-item-row-${rowIndex}` : undefined
    }
  >
    <span className={clsx(kBoxWidth, 'border-r-[1px] border-main pl-f1')} />
    <RowLayout showBalance={showBalance}>
      <SpanOrDropdown
        value={item.productCode}
        onChange={(value) => setProductCode?.(value)}
        options={productOptions}
        data-testid={
          rowIndex !== undefined
            ? `invoice-item-product-${rowIndex}`
            : undefined
        }
      />
      <SpanOrDropdown
        value={item.payoutUser}
        onChange={(value) => setPayoutUser?.(value)}
        options={payoutUserOptions}
        data-testid={
          rowIndex !== undefined ? `invoice-item-seller-${rowIndex}` : undefined
        }
      />
      {showBalance && item.balance !== undefined && (
        <CurrencyField
          amount={item.balance}
          currencyPrecision={item.currencyPrecision}
          currencyCode={item.currency}
          textSize="md"
          align="left"
        />
      )}

      <div className="flex flex-row w-full h-fit items-center justify-end gap-x-[1ch]">
        <EditableCurrencyField
          amount={item.amount}
          currencyPrecision={item.currencyPrecision}
          currencyCode={item.currency}
          setAmount={setPrice}
          setCurrency={setCurrency}
          data-testid={
            rowIndex !== undefined
              ? `invoice-item-amount-${rowIndex}`
              : undefined
          }
        />
        {removeInvoiceItem && (
          <TertiaryButton
            onClick={removeInvoiceItem}
            data-testid={
              rowIndex !== undefined
                ? `invoice-item-delete-${rowIndex}`
                : undefined
            }
          >
            <Icon type="trash" size="md" primaryColor="gray" />
          </TertiaryButton>
        )}
      </div>
      {item.balance === undefined && (
        <TextInputOrSpan
          value={item.description}
          onChange={(value) => setDescription?.(value)}
          placeholder="Description"
          data-testid={
            rowIndex !== undefined
              ? `invoice-item-description-${rowIndex}`
              : undefined
          }
        />
      )}
    </RowLayout>
  </div>
);

type DropdownOption = { value: string; label: string };

export type InvoiceItemTableProps = {
  invoiceItems: InvoiceRowItem[];
} & (
  | {
      removeInvoiceItem: (id: string) => void;
      onAddInvoiceItem: () => void;
      showBalance: false;
      // these would be the ids of existing items when adjusting an invoice
      // it allows us to prevent editing of item's seller, product code, or description when adjusting an invoice
      partialEditItemIds: string[];
      setPrice: (itemId: string, amount: number) => void;
      setCurrency: (itemId: string, currency: CurrencyCode) => void;
      setDescription?: (itemId: string, description: string) => void;
      setPayoutUser?: (itemId: string, payoutUser: string) => void;
      setProductCode?: (itemId: string, productCode: string) => void;
      productOptions?: DropdownOption[];
      // Function that returns payout party options for a given item
      // This allows filtering options based on the item's selected product
      getPayoutUserOptions: (item: InvoiceRowItem) => DropdownOption[];
    }
  | {
      showBalance: true;
    }
);

export const InvoiceItemTable = (props: InvoiceItemTableProps): JSX.Element => {
  const { showBalance, invoiceItems } = props;
  const tableView = useMemo(() => {
    if (showBalance) {
      return (
        <>
          {invoiceItems.map((item, index) => (
            <InvoiceItemRow
              key={item.id}
              item={item}
              showBalance={showBalance}
              rowIndex={index}
            />
          ))}
          <div className="flex flex-row w-full h-f4">
            <span
              className={clsx(kBoxWidth, 'border-r-[1px] border-main pl-f1')}
            />
          </div>
        </>
      );
    }
    const {
      removeInvoiceItem,
      onAddInvoiceItem,
      setPrice,
      setCurrency,
      setDescription,
      setPayoutUser,
      setProductCode,
      productOptions,
      getPayoutUserOptions,
      partialEditItemIds,
    } = props;

    return (
      <>
        {invoiceItems.map((item, index) => (
          <InvoiceItemRow
            key={item.id}
            item={item}
            showBalance={showBalance}
            rowIndex={index}
            removeInvoiceItem={() => removeInvoiceItem(item.id)}
            setPrice={(amount: number) => setPrice(item.id, amount)}
            setDescription={
              partialEditItemIds.includes(item.id)
                ? undefined
                : (description: string) =>
                    setDescription?.(item.id, description)
            }
            setPayoutUser={
              partialEditItemIds.includes(item.id)
                ? undefined
                : (payoutUser: string) => setPayoutUser?.(item.id, payoutUser)
            }
            setProductCode={
              partialEditItemIds.includes(item.id)
                ? undefined
                : (productCode: string) =>
                    setProductCode?.(item.id, productCode)
            }
            setCurrency={
              partialEditItemIds.includes(item.id)
                ? undefined
                : (currency: CurrencyCode) => setCurrency(item.id, currency)
            }
            productOptions={
              partialEditItemIds.includes(item.id) ? undefined : productOptions
            }
            payoutUserOptions={
              partialEditItemIds.includes(item.id)
                ? undefined
                : getPayoutUserOptions(item)
            }
          />
        ))}
        <div className="flex w-full justify-start font-FragmentMono">
          <span
            className={clsx(kBoxWidth, 'border-r-[1px] border-main pl-f1')}
          />
          <button
            type="button"
            data-testid="add-invoice-item-button"
            className="w-full h-f4 bg-main-200 text-main hover:bg-canvas"
            onClick={onAddInvoiceItem}
          >
            Add+
          </button>
        </div>
      </>
    );
  }, [showBalance, invoiceItems, props]);

  return (
    <div className="flex flex-col w-full h-fit">
      <div className="flex flex-row w-full h-fit">
        <span
          className={clsx(kBoxWidth, 'border-r-[1px] border-main pl-f1 pt-f1')}
        >
          Items
        </span>
        <RowLayout showBalance={showBalance} paddingTop>
          <span>Item</span>
          <div>Seller</div>
          {showBalance && <div>Balance</div>}
          <div className="text-right">Amount</div>
        </RowLayout>
      </div>

      {tableView}
    </div>
  );
};
